import type { ReplenishmentRow } from '@/features/decisions/decisions.api.types';

export function fmtUnitsK(n: number | null | undefined): string {
  if (n == null) return '—';
  if (Math.abs(n) >= 100_000) return `${Math.round(n / 1000).toLocaleString()}K`;
  if (Math.abs(n) >= 1_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

export function fmtMoneyK(qty: number | null | undefined, costPerUnit: number | null | undefined): string {
  if (qty == null || costPerUnit == null) return '—';
  const v = qty * costPerUnit;
  if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1000).toLocaleString()}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

export function fmtPercent(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${Math.round(n * 100)}%`;
}

export function fmtPlanningHorizon(lt: number | null, pbr: number | null): string {
  if (lt == null && pbr == null) return '—';
  const sum = (lt ?? 0) + (pbr ?? 0);
  return `${sum}w (${lt ?? 0}+${pbr ?? 0})`;
}

export type Delta = 'overstocked' | 'understocked' | 'aligned' | 'none';

export function classifyDelta(ref: number | null | undefined, cmp: number | null | undefined): Delta {
  if (ref == null || cmp == null) return 'none';
  if (ref === 0 && cmp === 0) return 'none';
  if (ref === 0) return 'understocked';
  if (cmp === 0) return 'overstocked';
  const pct = (cmp - ref) / ref;
  if (pct < -0.1) return 'overstocked';
  if (pct > 0.1) return 'understocked';
  return 'aligned';
}

export const DELTA_STYLES: Record<Delta, { bg: string; fg: string; border: string; label: string }> = {
  overstocked: { bg: 'bg-amber-50', fg: 'text-amber-700', border: 'border-amber-200', label: 'OVERSTOCKED' },
  understocked: { bg: 'bg-rose-50', fg: 'text-rose-700', border: 'border-rose-200', label: 'UNDERSTOCKED' },
  aligned: { bg: 'bg-emerald-50', fg: 'text-emerald-700', border: 'border-emerald-200', label: 'ALIGNED' },
  none: { bg: 'bg-gray-50', fg: 'text-gray-500', border: 'border-gray-200', label: '—' },
};

export interface GroupedRow {
  sku: string;
  site: string;
  ref: ReplenishmentRow | null;
  cmp: ReplenishmentRow | null;
}

// Groups (sku, site) rows; server returns sorted by (sku, site) then experiment,
// so Map insertion order preserves the desired output order.
export function groupRows(rows: ReplenishmentRow[], comparisonExperimentId: string): GroupedRow[] {
  const byKey = new Map<string, GroupedRow>();
  for (const r of rows) {
    const key = `${r.sku}__${r.site}`;
    let g = byKey.get(key);
    if (!g) {
      g = { sku: r.sku, site: r.site, ref: null, cmp: null };
      byKey.set(key, g);
    }
    if (r.is_reference) {
      g.ref = r;
    } else if (r.experiment_id === comparisonExperimentId) {
      g.cmp = r;
    } else if (!g.cmp) {
      g.cmp = r;
    }
  }
  return Array.from(byKey.values());
}
